/**
 * The dashboard's navigation tree — the single source of truth.
 *
 * Extracted from Layout/Sidebar.js so the shadcn `app-sidebar` renders exactly
 * the same items with the same permissions. Two hand-maintained copies would
 * drift the first time someone adds a page.
 *
 * Shape: { title, path, icon, permission, subItems?: [{ title, path, permission }] }
 * `permission` is checked with useAuth().hasPermission before an item renders.
 */

import {
  LayoutDashboard,
  Users,
  UserCheck,
  Car,
  Shield,
  Settings,
  FileText,
  Building2,
  CreditCard,
  MessageSquare,
  MapPin,
  Clock,
  Sliders,
} from 'lucide-react';

/**
 * Glyph icons for entries lucide has no good match for.
 *
 * These were inline arrow functions that closed over the old sidebar's
 * `sidebarCollapsed` state, which is why the menu could not live outside that
 * component. As components taking `className`, each sidebar styles them its
 * own way and the data stays portable.
 */
const glyph = (char) => {
  const Glyph = ({ className = '' }) => (
    <span className={`flex h-5 w-5 flex-shrink-0 items-center justify-center text-base ${className}`}>
      {char}
    </span>
  );
  return Glyph;
};

const RupeeIcon = glyph('₹');
const SimulatorIcon = glyph('🎮');
const CrownIcon = glyph('👑');
const GiftIcon = glyph('🎁');

export const menuItems = [
    {
      title: 'Overview',
      path: '/dashboard',
      icon: LayoutDashboard,
      permission: 'dashboard'
    },
    {
      title: 'Drivers',
      path: '/drivers',
      icon: Users,
      permission: 'drivers',
      subItems: [
        {
          title: 'Create Driver',
          path: '/drivers/create',
          permission: 'drivers'
        },
        {
          title: 'List Drivers',
          path: '/drivers',
          permission: 'drivers'
        },
        // {
        //   title: 'Driver Attendance',
        //   path: '/drivers/attendance',
        //   permission: 'drivers'
        // },
        {
          title: 'Drivers Ridematchs',
          path: '/drivers/ridematchs',
          permission: 'drivers'
        },
        {
          title: 'Driver Work History',
          path: '/drivers/driverworkhistory',
          permission: 'drivers'
        },
        {
          title: 'Driver Approval',
          path: '/drivers/pending',
          permission: 'drivers'
        },
        {
          title: 'Driver Stats',
          path: '/drivers/stats',
          permission: 'drivers'
        }
      ]
    },
    {
      title: 'Passengers',
      path: '/passengers',
      icon: UserCheck,
      permission: 'passengers',
      subItems: [
        {
          title: 'View Passengers',
          path: '/passengers',
          permission: 'passengers'
        }
      ]
    },
    {
      title: 'Trips',
      path: '/trips',
      icon: Car,
      permission: 'trips',
      subItems: [
        {
          title: 'Create Trips',
          path: '/trips/create',
          permission: 'trips'
        },
        {
          title: 'Normal Trips',
          path: '/trips',
          permission: 'trips'
        },
        {
          title: 'Acting Trips',
          path: '/trips/acting',
          permission: 'trips'
        },
        {
          title: 'Schedule Acting Drivers',
          path: '/trips/schedule',
          permission: 'trips'
        }
      ]
    },
    {
      title: 'Hotspot',
      path: '/hotspot',
      icon: MapPin,
      permission: 'hotspot',
      subItems: [
        {
          title: 'List Hotspot',
          path: '/hotspot',
          permission: 'hotspot'
        }
      ]
    },
    {
      title: 'Peak Hours',
      path: '/peak-hours',
      icon: Clock,
      permission: 'peakhours'
    },
    {
      title: 'Regional Offices',
      path: '/regional-offices',
      icon: Building2,
      permission: 'regional_offices',
      subItems: [
        {
          title: 'Create Office',
          path: '/regional-offices/create',
          permission: 'regional_offices'
        },
        {
          title: 'List Offices',
          path: '/regional-offices',
          permission: 'regional_offices'
        }
      ]
    },
    {
      title: 'Vendors',
      path: '/vendors',
      icon: Building2,
      permission: 'vendors',
      subItems: [
        {
          title: 'Create Vendor',
          path: '/vendors/create',
          permission: 'vendors'
        },
        {
          title: 'List Vendors',
          path: '/vendors',
          permission: 'vendors'
        }
      ]
    },
    {
      title: 'Vehicles',
      path: '/vehicles',
      icon: Car,
      permission: 'vehicles',
      subItems: [
        {
          title: 'Create Vehicle',
          path: '/vehicles/create',
          permission: 'vehicles'
        },
        {
          title: 'List Vehicles',
          path: '/vehicles',
          permission: 'vehicles'
        },
        {
          title: 'Switch Requests',
          path: '/vehicles/switch-requests',
          permission: 'vehicles'
        }
      ]
    },
    {
      title: 'Tickets',
      path: '/tickets',
      icon: FileText,
      permission: 'tickets'
    },
    {
      title: 'Role Management',
      path: '/roles',
      icon: Shield,
      permission: 'roles'
    },

    {
      title: 'Fare Configuration',
      path: '/fare',
      icon: RupeeIcon,
      permission: 'fare'
    },
    {
      title: 'Acting Driver Fare',
      path: '/acting-driver-fare',
      icon: RupeeIcon,
      permission: 'actingDriverFare'
    },
    {
      title: 'Referral Program',
      path: '/referral',
      icon: GiftIcon,
      permission: 'appconfig'
    },
    {
      title: 'Driver Simulator',
      path: '/driver-sim',
      icon: SimulatorIcon,
      permission: 'driverSimulator'
    },
    {
      title: 'Subscription Plan',
      path: '/subscription-plan',
      icon: CrownIcon,
      permission: 'SubscriptionPlan'
    },
    // {
    //   title: 'Payments',
    //   path: '/payments',
    //   icon: CreditCard,
    //   permission: 'payments',
    //   subItems: [
    //     {
    //       title: 'Earnings',
    //       path: '/payments/earnings',
    //       permission: 'payments'
    //     },
    //     {
    //       title: 'Due',
    //       path: '/payments/due',
    //       permission: 'payments'
    //     }
    //   ]
    // },
    {
      title: 'Financial Reports',
      path: '/financial',
      icon: CreditCard,
      permission: 'financial',
      subItems: [
        {
          title: 'Due Payments',
          path: '/financial/due',
          permission: 'financial'
        },
        {
          title: 'Earnings',
          path: '/financial/earnings',
          permission: 'financial'
        },
        {
          title: 'GST',
          path: '/financial/gst',
          permission: 'financial',
        }
      ]
    },
    {
      title: 'App Config',
      path: '/config',
      icon: Settings,
      permission: 'appconfig'
    },
    {
      title: "Feedback",
      path: "/feedback",
      icon: MessageSquare,
      permission: "feedback"
    },
    {
      title: 'Settings',
      path: '/settings',
      icon: Settings,
      permission: 'settings'
    },
    {
      title: 'Ride Match Settings',
      path: '/ride-match-settings',
      icon: Sliders,
      permission: 'rideMatchSettings'
    },
    {
      title: 'Discord Notifications',
      path: '/discord-settings',
      icon: MessageSquare,
      permission: 'settings'
    }
  ];
